import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPrint } from 'react-icons/fa';
import InfrastructureLeasingBuyerPostSaleMilestones from '../../components/infrastructureLeasing/InfrastructureLeasingBuyerPostSaleMilestones';
import InfrastructureLeasingBuyerPostSaleTabs from '../../components/infrastructureLeasing/InfrastructureLeasingBuyerPostSaleTabs';
import {
  fetchBuyerInfrastructureAgreementPage,
  fetchBuyerPostSaleSnapshot,
} from '../../services/infrastructureLeasingListing.service';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_leasing_buyer_post_sale.scss';
import '../../assets/styles/infrastructure_leasing_buyer_invoice.scss';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatDateShort(iso) {
  if (!iso) return '—';
  try {
    const d = String(iso).slice(0, 10);
    return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function PartyCard({ heading, party }) {
  if (!party) {
    return (
      <div className="ile-invoice-document__party-box">
        <div className="ile-invoice-document__party-heading">{heading}</div>
        <div className="text-muted">—</div>
      </div>
    );
  }
  const d = party.displayName != null ? String(party.displayName).trim() : '';
  const c = party.companyName != null ? String(party.companyName).trim() : '';
  const primary = d || c || '—';
  const secondary = c && d && c !== d ? c : null;
  return (
    <div className="ile-invoice-document__party-box">
      <div className="ile-invoice-document__party-heading">{heading}</div>
      <div className="ile-invoice-document__party-name">{primary}</div>
      {secondary ? <div className="text-secondary">{secondary}</div> : null}
      {party?.email ? (
        <div>
          <span className="text-muted">Email:</span> {party.email}
        </div>
      ) : null}
      {party?.phone ? (
        <div>
          <span className="text-muted">Phone:</span> {party.phone}
        </div>
      ) : null}
      {party?.addressLine ? (
        <div className="mt-1 text-break">
          <span className="text-muted">Address:</span> {party.addressLine}
        </div>
      ) : null}
    </div>
  );
}

export default function InfrastructureLeasingBuyerInvoicePage() {
  const { listingId: listingParam, enquiryId: enquiryParam } = useParams();
  const navigate = useNavigate();
  const listingIdNum = useMemo(() => {
    const n = parseInt(String(listingParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [listingParam]);
  const enquiryIdNum = useMemo(() => {
    const n = parseInt(String(enquiryParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [enquiryParam]);

  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  /** Same payload as agreement page — parties, asset, lease terms */
  const [agreementPayload, setAgreementPayload] = useState(null);

  const basePath = `/infrastructure-leasing/my-enquiries/${listingIdNum}/${enquiryIdNum}`;

  const load = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setAgreementPayload(null);
    try {
      const res = await fetchBuyerPostSaleSnapshot(listingIdNum, enquiryIdNum);
      if (res?.success && res.data) {
        setSnapshot(res.data);
        const st = String(res.data.pipelineStage || '').toLowerCase();
        if (st === 'payment_due') {
          navigate(`${basePath}/payment`, { replace: true });
          return;
        }
        try {
          const agrRes = await fetchBuyerInfrastructureAgreementPage(listingIdNum, enquiryIdNum);
          if (agrRes?.success && agrRes.data) {
            setAgreementPayload(agrRes.data);
          }
        } catch {
          /* invoice still usable from snapshot only */
        }
      } else {
        setSnapshot(null);
        toast.error(res?.message || 'Could not load invoice.');
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message;
      if (status === 409) {
        toast.warning(msg || 'Complete agreement signing first.');
        navigate(`${basePath}/agreement`, { replace: true });
      } else {
        toast.error(msg || 'Could not load invoice.');
      }
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [listingIdNum, enquiryIdNum, navigate, basePath]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePrint = () => {
    window.print();
  };

  if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
    return (
      <div className="container-fluid ile-buyer-post-sale py-4">
        <p className="text-muted">Invalid link.</p>
        <Link to="/infrastructure-leasing/my-enquiries">Back to My enquiries</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid ile-buyer-post-sale py-4">
        <div className="text-muted py-5">Loading invoice…</div>
      </div>
    );
  }

  const stage = String(snapshot?.pipelineStage || '').toLowerCase();
  const hasInvoice = snapshot?.invoice?.reference || snapshot?.invoice?.issuedAt;

  const agr = agreementPayload?.agreement;
  const seller = agreementPayload?.seller;
  const buyer = agreementPayload?.buyer;
  const asset = agreementPayload?.asset;
  const enquirySection = agreementPayload?.enquirySection;
  const listingTitle =
    snapshot?.listingTitle || agreementPayload?.listingTitle || agreementPayload?.listing?.title || null;

  const lineDescription = `Initial lease payment — ${agr?.paymentFrequencyLabel || agr?.paymentFrequency || 'per agreed frequency'}`;
  const lineDetailParts = [];
  if (agr?.leaseStartDate && agr?.leaseEndDate) {
    lineDetailParts.push(`Lease period: ${formatDateShort(agr.leaseStartDate)} → ${formatDateShort(agr.leaseEndDate)}`);
  }
  if (agr?.leaseDurationMonths != null) {
    lineDetailParts.push(`${agr.leaseDurationMonths} month(s)`);
  }
  if (snapshot?.agreementReference || agr?.agreementReference) {
    lineDetailParts.push(`Agreement: ${snapshot?.agreementReference || agr?.agreementReference}`);
  }
  const lineDetail = lineDetailParts.filter(Boolean).join(' · ');

  return (
    <div className="container-fluid ile-buyer-post-sale py-4">
      <div className="ile-buyer-post-sale__shell">
        <nav aria-label="breadcrumb" className="mb-3 ile-invoice-print-hide">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/dashboard/buyer">Buyer dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/infrastructure-leasing/my-enquiries">My enquiries</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Invoice
            </li>
          </ol>
        </nav>

        <div className="ile-invoice-print-hide">
          <InfrastructureLeasingBuyerPostSaleTabs basePath={basePath} />
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3 ile-invoice-print-hide">
          <div>
            <h1 className="h4 mb-1">Invoice</h1>
            <p className="text-muted small mb-0">
              {listingTitle ? (
                <>
                  <span className="fw-semibold text-body">{listingTitle}</span>
                  {snapshot?.agreementReference ? (
                    <span className="text-muted"> · {snapshot.agreementReference}</span>
                  ) : null}
                </>
              ) : (
                'Infrastructure leasing'
              )}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {hasInvoice ? (
              <button type="button" className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2" onClick={handlePrint}>
                <FaPrint aria-hidden /> Print / PDF
              </button>
            ) : null}
          </div>
        </div>

        {snapshot?.milestones?.length ? (
          <div className="ile-invoice-print-hide mb-4">
            <InfrastructureLeasingBuyerPostSaleMilestones milestones={snapshot.milestones} />
          </div>
        ) : null}

        {hasInvoice ? (
          <div className="ile-invoice-document mb-4" id="ile-invoice-print-root">
            <div className="ile-invoice-document__banner">
              <h2 className="ile-invoice-document__banner-title mb-0">Tax invoice</h2>
              <div className="ile-invoice-document__banner-meta">
                <div>Infrastructure &amp; asset leasing</div>
                <div className="small mt-1 opacity-75">Trocanet B2B marketplace</div>
              </div>
            </div>

            <div className="ile-invoice-document__body">
              <div className="ile-invoice-document__inv-number">
                Invoice no. <span className="text-primary">{snapshot.invoice.reference || '—'}</span>
              </div>

              <div className="ile-invoice-document__meta-grid">
                <div>
                  <span className="ile-invoice-document__meta-label">Invoice date</span>
                  <div>{formatWhen(snapshot.invoice.issuedAt)}</div>
                </div>
                <div>
                  <span className="ile-invoice-document__meta-label">Agreement reference</span>
                  <div>{snapshot.agreementReference || agr?.agreementReference || '—'}</div>
                </div>
                <div>
                  <span className="ile-invoice-document__meta-label">Enquiry ID</span>
                  <div>{enquirySection?.enquiryId != null ? `#${enquirySection.enquiryId}` : `#${enquiryIdNum}`}</div>
                </div>
                <div>
                  <span className="ile-invoice-document__meta-label">Listing ID</span>
                  <div>#{agreementPayload?.infrastructureListingId ?? listingIdNum}</div>
                </div>
                <div>
                  <span className="ile-invoice-document__meta-label">Payment status</span>
                  <div>
                    <span className="badge text-bg-success">Paid in full</span>
                  </div>
                </div>
              </div>

              <div className="ile-invoice-document__parties">
                <PartyCard heading="Bill from (Lessor / Seller)" party={seller} />
                <PartyCard heading="Bill to (Lessee / Buyer)" party={buyer} />
              </div>

              {(asset?.assetName || asset?.locationLine) && (
                <div className="ile-invoice-document__subject">
                  <div className="fw-semibold text-dark mb-1">Subject / Asset</div>
                  <div>{asset?.assetName || listingTitle || '—'}</div>
                  {asset?.assetTypeLabel ? (
                    <div className="text-muted small">Type: {asset.assetTypeLabel}</div>
                  ) : null}
                  {asset?.locationLine ? (
                    <div className="text-muted small mt-1">Location: {asset.locationLine}</div>
                  ) : null}
                </div>
              )}

              <div className="ile-invoice-document__table-wrap">
                <table className="table table-bordered ile-invoice-document__table mb-0">
                  <thead>
                    <tr>
                      <th scope="col" style={{ width: '58%' }}>
                        Description
                      </th>
                      <th scope="col" style={{ width: '42%' }} className="text-end">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="fw-semibold text-body">{lineDescription}</div>
                        {lineDetail ? <div className="small text-muted mt-1">{lineDetail}</div> : null}
                        {agr?.rentalAmount ? (
                          <div className="small text-muted mt-1">
                            Agreed rental reference: <span className="text-body">{agr.rentalAmount}</span>
                            {agr.securityDeposit ? (
                              <>
                                {' '}
                                · Security deposit (per agreement): {agr.securityDeposit}
                              </>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="ile-invoice-document__amount-cell align-middle">
                        {snapshot.invoice.amountLabel || agr?.rentalAmount || '—'}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="text-end text-uppercase small text-muted">Total (this invoice)</td>
                      <td className="ile-invoice-document__amount-cell">{snapshot.invoice.amountLabel || '—'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="ile-invoice-document__payment-strip">
                <div className="row g-2 small">
                  <div className="col-sm-4">
                    <span className="text-muted d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                      Paid at
                    </span>
                    {formatWhen(snapshot.payment?.paidAt)}
                  </div>
                  <div className="col-sm-4">
                    <span className="text-muted d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                      Payment method
                    </span>
                    {snapshot.payment?.methodLabel || '—'}
                  </div>
                  <div className="col-sm-4">
                    <span className="text-muted d-block text-uppercase" style={{ fontSize: '0.7rem' }}>
                      Invoice issued
                    </span>
                    {formatWhen(snapshot.invoice.issuedAt)}
                  </div>
                </div>
              </div>

              <div className="ile-invoice-document__footer-note">
                This document is generated electronically for the initial lease payment recorded on Trocanet. For
                disputes or GST-specific invoicing, contact the lessor using the details above. Retain this for your
                records.
              </div>
            </div>
          </div>
        ) : (
          <div className="card border shadow-sm mb-3">
            <div className="card-body text-muted small">
              Invoice is not available yet.{' '}
              <Link to={`${basePath}/payment`}>Go to payment</Link>
            </div>
          </div>
        )}

        <div className="d-flex flex-wrap gap-2 align-items-center ile-invoice-print-hide">
          {stage === 'invoice_issued' || stage === 'lease_active' ? (
            <Link to={`${basePath}/lease-tracking`} className="btn btn-dark">
              Go to Lease Tracking
            </Link>
          ) : null}
          <Link to="/infrastructure-leasing/my-enquiries" className="btn btn-outline-secondary btn-sm">
            My enquiries
          </Link>
        </div>
      </div>
    </div>
  );
}
